import { fetchJsonWithAuth, apiGetWithAuth } from './authApi';
import { AUTH_URL } from './apiConfig';

async function authapiGet(path) {
  return apiGetWithAuth(AUTH_URL, path);
}

async function authapiPost(path, payload) {
  const json = await fetchJsonWithAuth(AUTH_URL, path, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return json.data ?? json;
}

async function authapiPut(path, payload) {
  const json = await fetchJsonWithAuth(AUTH_URL, path, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return json.data ?? json;
}

async function authapiDelete(path) {
  const json = await fetchJsonWithAuth(AUTH_URL, path, { method: 'DELETE' });
  return json.data ?? json;
}

// ── Testing Options ───────────────────────────────────────────────────────────

export async function getTestingOptions(type) {
  return authapiGet(`/testing-modules?type=${encodeURIComponent(type)}`);
}

export async function addTestingOption({ name, type }) {
  return authapiPost('/testing-modules', { name, type });
}

export async function getActionFieldRules() {
  return authapiGet('/test-action-field-rules');
}

// ── Test Flows ────────────────────────────────────────────────────────────────

export async function addTestFlow(payload) {
  return authapiPost('/test-flows', payload);
}

export async function getTestFlows({
  limit = 10, offset = 0, search = '', module = '',
  keyword = '', tags = '', active,
} = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (search) params.append('search', search);
  if (module) params.append('module', module);
  if (keyword) params.append('keyword', keyword);
  if (tags) params.append('tags', tags);
  if (active !== undefined && active !== null && active !== '') params.append('active', String(active));
  return authapiGet(`/test-flows?${params.toString()}`);
}

// ── Test Steps ────────────────────────────────────────────────────────────────

export async function validateTestSteps(payload) {
  return authapiPost('/test-steps/validate', payload);
}

export async function bulkInsertTestSteps(payload) {
  return authapiPost('/test-steps/bulk-insert', payload);
}

// ── LMS Flows ─────────────────────────────────────────────────────────────────

export async function addLmsFlow(payload) {
  return authapiPost('/lms-flows', payload);
}

export async function getLmsFlows({
  limit = 10, offset = 0, search = '', module = '',
  keyword = '', tags = '', active,
} = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (search) params.append('search', search);
  if (module) params.append('module', module);
  if (keyword) params.append('keyword', keyword);
  if (tags) params.append('tags', tags);
  if (active !== undefined && active !== null && active !== '') params.append('active', String(active));
  return authapiGet(`/lms-flows?${params.toString()}`);
}

// ── CT Projects ───────────────────────────────────────────────────────────────

export async function getCTProjects({ activeOnly = false } = {}) {
  const params = new URLSearchParams();
  if (activeOnly) params.append('active', '1');
  const query = params.toString();
  return authapiGet(`/ct/projects${query ? `?${query}` : ''}`);
}

export async function addCTProject(projectname) {
  return authapiPost('/ct/projects', { projectname });
}

export async function setCTProjectStatus(id, active) {
  return authapiPut('/ct/projects/status', { id, active });
}

// ── CT Modules ────────────────────────────────────────────────────────────────

export async function getCTModules({ project = '', activeOnly = false } = {}) {
  const params = new URLSearchParams();
  if (project) params.append('project', project);
  if (activeOnly) params.append('active', '1');
  const query = params.toString();
  return authapiGet(`/ct/modules${query ? `?${query}` : ''}`);
}

export async function addCTModule({ project, modulename }) {
  return authapiPost('/ct/modules', { project, modulename });
}

export async function setCTModuleStatus(id, active) {
  return authapiPut('/ct/modules/status', { id, active });
}

// ── CT Submodules ─────────────────────────────────────────────────────────────

export async function getCTSubmodules({ project = '', module = '', activeOnly = false } = {}) {
  const params = new URLSearchParams();
  if (project) params.append('project', project);
  if (module) params.append('module', module);
  if (activeOnly) params.append('active', '1');
  const query = params.toString();
  return authapiGet(`/ct/submodules${query ? `?${query}` : ''}`);
}

export async function addCTSubmodule({ project, modulename, submodulename }) {
  return authapiPost('/ct/submodules', { project, modulename, submodulename });
}

export async function setCTSubmoduleStatus(id, active) {
  return authapiPut('/ct/submodules/status', { id, active });
}

// ── CT Flows ──────────────────────────────────────────────────────────────────

export async function getCTFlows({
  project = '', module = '', submodule = '',
  product_parent_ID = '', module_parent_ID = '', submodule_parent_ID = '',
  actions = '', search = '', active, limit = 1000, offset = 0,
} = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const productParentID = product_parent_ID || project;
  const moduleParentID = module_parent_ID || module;
  const submoduleParentID = submodule_parent_ID || submodule;
  if (productParentID) params.append('product_parent_ID', productParentID);
  if (moduleParentID) params.append('module_parent_ID', moduleParentID);
  if (submoduleParentID) params.append('submodule_parent_ID', submoduleParentID);
  if (actions) params.append('actions', actions);
  if (search) params.append('search', search);
  if (active !== undefined && active !== null && active !== '') params.append('active', String(active));
  return authapiGet(`/ct/flows?${params.toString()}`);
}

export async function addCTFlow(payload) {
  return authapiPost('/ct/flows', payload);
}

export async function getCTFlow(id) {
  return authapiGet(`/ct/flows/${encodeURIComponent(id)}`);
}

export async function getCTNextStep({ project, module, submodule, product_parent_ID, module_parent_ID, submodule_parent_ID }) {
  const params = new URLSearchParams({
    product_parent_ID: String(product_parent_ID || project),
    module_parent_ID: String(module_parent_ID || module),
    submodule_parent_ID: String(submodule_parent_ID || submodule),
  });
  return authapiGet(`/ct/flows/next-step?${params.toString()}`);
}

export async function updateCTFlow(id, payload) {
  return authapiPut(`/ct/flows/${encodeURIComponent(id)}`, payload);
}

export async function deleteCTFlow(id) {
  return authapiDelete(`/ct/flows/${encodeURIComponent(id)}`);
}
